/**
 * What: Defines merchant-facing relative previews for fixed Shopify shopper questions.
 * Transport / data-flow: Pure copy consumed by the Shopify study builder through productLanguage.
 *
 */
export const shopifyStudyQuestionsLanguage = {
    title: 'Shopper questions',
    label: 'What the test asks after shoppers see your product.',
    packagingClarityTitle: 'Packaging clarity',
    offerClarityTitle: 'Offer clarity',
    offerPurchaseTitle: 'Buying interest with the offer',
    offerQuestions: 'Each shopper sees one offer with the product information. The main result measures stated interest in buying on the shown terms. Offer clarity and overall reaction are separate. This does not measure actual sales, redemption or the extra purchases caused by the promotion.',
    packagingQuestions: 'Each version must show the packaging, including the original. Shoppers see the product information and price alongside it. The main result compares pack-design reactions; packaging clarity and product buying interest are separate. This does not test understanding from the image alone.',
    addedMessageQuestions: 'These questions compare the whole product, with and without your added message. The original product has no added message.',
    conceptQuestions: {
        brand_tagline: {
            construct: 'clarity',
            label: 'Brand Hook clarity',
            text: 'What, if anything, does the wording shown communicate about the product’s brand, and what is unclear?',
            rationale: 'Explores what shoppers take from the hook or brand statement.',
        },
        taste_sensory: {
            construct: 'expectations',
            label: 'Sensory expectations',
            text: 'What taste, scent or texture, if any, would you expect from the product information shown, and why?',
            rationale: 'Explores the sensory expectations the description creates.',
        },
    },
    body: 'TestFeed uses the same question structure for each test, so results are easier to interpret and compare when the setup is similar.',
    methodNote: 'TestFeed scores written shopper reactions against benchmarked response patterns. These are simulated shoppers, not a survey of your actual customers. Results are directional. They compare versions inside this test only.',
    questionPreviewLabel: 'Question preview',
    infoLabel: 'Method note',
    infoSymbol: 'i',
    cards: {
        understanding: {
            title: 'What shoppers understand',
            bodies: {
                default: 'Can shoppers explain what the product or offer is, and why it might matter to them?',
                claim: 'Can shoppers explain what the claim says about the product?',
                brand_tagline: 'Can shoppers explain what the brand statement or hook communicates about the product?',
                taste_sensory: 'Can shoppers explain what the flavour, scent, or sensory description communicates about the product?',
                usage_experience: 'Can shoppers explain what steps or routine are required to use, apply, or care for the product?',
                ingredient_proof: 'Can shoppers explain what role this specific ingredient, formula, or material plays in the product?',
                price: 'Can shoppers explain what the product or offer is, and why it might matter to them?',
                packaging: 'How clear or unclear does the packaging seem with the accompanying product information available?',
                image_creative: 'Can shoppers explain what the image shows and what product or offer it communicates?',
                promotion_mechanic: 'How clear or unclear are the offer benefit and requirements?',
            },
            rationale: 'Checks whether the idea is clear before judging appeal.',
            tooltip: 'Comprehension is checked before interpreting the buying signal.',
        },
        purchaseIntent: {
            title: 'Whether shoppers would buy',
            bodies: {
                default: 'What would make shoppers consider buying, or hold them back?',
                claim: 'How, if at all, does the claim affect whether shoppers would consider buying?',
                brand_tagline: 'How, if at all, does this brand statement or hook influence whether shoppers would consider buying?',
                taste_sensory: 'How, if at all, does this taste, aroma, or sensory description influence whether shoppers would consider buying?',
                usage_experience: 'How does the required application, preparation, or care routine affect whether shoppers would consider buying?',
                ingredient_proof: 'How does knowing this formulation or ingredient detail influence whether shoppers would consider buying?',
                price: 'What would make shoppers consider buying, or hold them back?',
                packaging: 'How likely are shoppers to buy the product, and what shapes that decision?',
                image_creative: 'How, if at all, does the image affect buying consideration, and which visual elements shape that?',
                promotion_mechanic: 'How likely are shoppers to buy on the offer terms shown?',
            },
            rationale: 'Keeps the reasons behind purchase intent attached to the result.',
            tooltip: 'Purchase intent is inferred from written reactions rather than direct ratings.',
        },
        impression: {
            title: 'Overall reaction',
            bodies: {
                default: 'How do shoppers describe the product, offer, quality, and appeal overall?',
                claim: 'How do shoppers describe the claim’s clarity, credibility, and appeal?',
                brand_tagline: 'How do shoppers describe the hook or brand statement’s resonance and appeal?',
                taste_sensory: 'How do shoppers describe the flavour profile, sensory appeal, and finish?',
                usage_experience: 'How do shoppers describe the convenience, ease of use, and lifestyle fit of the product?',
                ingredient_proof: 'How credible and compelling do shoppers find this ingredient or formulation proof?',
                price: 'How do shoppers describe the product, offer, quality, and appeal overall?',
                packaging: 'What is the overall reaction to this pack design, positively or negatively? This is the main result.',
                image_creative: 'What stands out in the image, positively or negatively?',
                promotion_mechanic: 'What is the overall reaction to this promotional offer, positively or negatively?',
            },
            rationale: 'Helps explain whether the full offer feels coherent.',
            tooltip: 'Broader reaction helps explain the directional result.',
        },
        custom: {
            title: 'Your question',
            body: 'Your own question. Answers come back as written replies you can read, and do not change the test score.',
            rationale: 'Written replies add context behind the result without affecting how it is scored.',
            tooltip: 'Your questions are read alongside the result, not counted into it.',
        },
        fallback: {
            title: 'Shopper reaction',
            body: 'Captures an open-ended shopper reaction used in the test result.',
            rationale: 'Open-ended responses preserve the reason behind the result instead of relying only on a direct rating.',
            tooltip: 'Open-ended responses keep the result explainable.',
        },
    },
    results: {
        heading: 'Shopper answers',
        description: 'Read the original answers to each question, across the versions tested.',
        standardQuestion: 'Standard question',
        merchantQuestion: 'Your question',
        usedInScore: 'Answers to this question contribute to the test score.',
        attachmentNote: 'Attachment checks are separate from the test score. They show what was reported about the supplied image; they do not confirm that every detail was understood.',
        /**
         * Required on this surface, rendered above the answers and never behind a disclosure.
         * Answers carry human-sounding names and avatars, and the existing disclaimer lives inside
         * the Reactions tab, so this tab inherits nothing.
         */
        syntheticOrigin: 'These are simulated shoppers, not a survey of your actual customers. Read them for the reasoning behind the result.',
        notScored: 'These answers do not affect the test score.',
        emptyState: 'No saved answers are available for this test yet.',
        answersShown: 'answers shown of',
        loadingAnswers: 'Loading answers',
        total: 'total',
    },
    custom: {
        title: 'Ask your own question',
        label: 'Add up to 3 questions of your own. Shoppers answer in their own words.',
        placeholder: 'What would make you hesitate before ordering this?',
        addAction: 'Add question',
        removeAction: 'Remove',
        /** The accessible name for one own question's Remove button, so each one is distinct. `{n}` is the question number. */
        removeQuestionAccessibility: 'Remove question {n}',
        charactersRemaining: 'characters left',
        questionsUsed: 'of 3 questions added',
        acknowledgedTitle: 'Acknowledged',
        reviewRequiredTitle: 'Needs Acknowledgement',
        useAnywayLabel: 'Use this question anyway',
        warningTitle: 'Warning / Recommendation',
        recommendations: {
            heading: 'Tick to add a written-answer question. These answers do not change the test score.',
            subheading: 'Suggested questions for this comparison. Shoppers answer in their own words.',
            addAction: 'Add recommendation',
            addedBadge: 'Added',
            alreadyAdded: 'This recommended question is in your test.',
        },
        /**
         * Merchant-facing text for rubric findings, keyed by messageKey.
         *
         * The Worker returns ruleId + messageKey only, never prose, so every string a merchant reads
         * is written here and stays free of methodology vocabulary.
         */
        rubricMessages: {
            numericScale: 'Shoppers are never asked for a number, score or star rating — being asked for one changes how they answer everything else. Ask what they think in their own words instead.',
            structuralIdentity: 'This question can be read alongside your results but cannot change the score, so it needs to be a written-answer question.',
            promptBudget: 'This is longer than shoppers reliably answer well. Shorter questions get better answers.',
            inventedHistory: 'Simulated shoppers have no real-world history, so answers about what they have bought, used, heard of or searched for before would be made up.',
            promptOverride: 'This looks like an instruction rather than a question a shopper would answer. Try rewording it as something you would ask a customer.',
            closedQuestion: 'This can be answered with yes or no. Starting with "what" or "how" will get you more useful answers.',
            doubleBarrelled: 'This asks two things at once. Splitting it into separate questions gives clearer answers.',
        },
        /** Advisory notes when a question reads like a different kind of study. Never blocking. */
        suggestedRouteMessages: {
            pricing: 'This reads like a pricing question. Shoppers can react to a price you show them, but any price they invent themselves would not be reliable.',
            comparative: 'This reads like a head-to-head comparison. Each shopper only sees one version, so set up the other version as its own test and compare the results.',
            feature_priority: 'This reads like a ranking question. Ranking needs a different kind of test, which is not available yet.',
            conversion_lift: 'This reads like a question about what shoppers would actually click or buy. This test measures what they say, not what they do.',
        },
    },
};
//# sourceMappingURL=shopify-study-questions.js.map